import React, { useCallback, useEffect, useRef, useState } from 'react';
import store from './journalStore';

/* 🌌 Written in the Stars — a living night sky where every day with content
   (notes, photos, or a big day) becomes a star, and the stars connect —
   chronologically — into a heart-shaped constellation that draws itself in.
   Tap a star to see that day. The sky is painted on a DPR-aware canvas:
   gradient night, drifting aurora, twinkling stars, a glowing moon and the
   occasional shooting star. Honors prefers-reduced-motion (static sky). */

const prettyDate = (ds) => {
  const [y, m, dd] = ds.split('-').map(Number);
  return new Date(y, m - 1, dd).toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
};
const shortDate = (ds) => {
  const [y, m, dd] = ds.split('-').map(Number);
  return new Date(y, m - 1, dd).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/* classic parametric heart; flipped so +y goes down like canvas coords.
   t=0 is the cleft between the lobes, so our story starts at the top of the
   heart and travels all the way around it. */
const heartPoint = (t) => ({
  x: 16 * Math.sin(t) ** 3,
  y: -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t)),
});

const TWO_PI = Math.PI * 2;

export default function Stars({ passcode }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [memories, setMemories] = useState(null); // null = loading
  const [selected, setSelected] = useState(null); // index into memories
  const [err, setErr] = useState('');

  const sceneRef = useRef(null); // { w, h, ambient[], pts[], lens[], totalLen, moon }
  const hoverRef = useRef(-1);
  const shootsRef = useRef({ list: [], next: 0 });
  const startRef = useRef(0);
  const rafRef = useRef(0);
  const selectedRef = useRef(null);
  selectedRef.current = selected;

  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---- data: one star per day that has anything ----
  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const [{ entriesByDate, photos, voices }, events] = await Promise.all([
          store.all(passcode),
          store.events(passcode),
        ]);
        const byDate = new Map();
        const at = (d) => {
          if (!byDate.has(d)) byDate.set(d, { date: d, notes: 0, photos: 0, voices: 0, event: null });
          return byDate.get(d);
        };
        Object.entries(entriesByDate || {}).forEach(([d, arr]) => {
          at(d).notes = (arr || []).length;
        });
        (photos || []).forEach((p) => {
          at(p.date).photos += 1;
        });
        (voices || []).forEach((v) => {
          at(v.date).voices += 1;
        });
        (events || []).forEach((ev) => {
          const m = at(ev.date);
          if (!m.event) m.event = { title: ev.title, emoji: ev.emoji };
        });
        const list = [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
        if (!gone) setMemories(list);
      } catch (e) {
        if (!gone) {
          setErr('The sky is shy right now — try again in a moment.');
          setMemories([]);
        }
      }
    })();
    return () => {
      gone = true;
    };
  }, [passcode]);

  // ---- build the scene (positions only; drawing happens per-frame) ----
  const buildScene = useCallback(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas || !memories) return;
    const w = wrap.clientWidth;
    const h = Math.max(380, Math.min(window.innerHeight * 0.68, 620));
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    // deterministic-ish pseudo-random so rebuilds don't reshuffle wildly
    let seed = 1404;
    const rnd = () => {
      seed = (seed * 16807) % 2147483647;
      return seed / 2147483647;
    };

    const ambient = Array.from({ length: Math.round((w * h) / 5200) }, () => ({
      x: rnd() * w,
      y: rnd() * h * 0.94,
      r: 0.4 + rnd() * 1.5,
      base: 0.25 + rnd() * 0.55,
      amp: 0.12 + rnd() * 0.3,
      speed: 0.0006 + rnd() * 0.0016,
      phase: rnd() * TWO_PI,
      glint: rnd() > 0.94,
    }));

    // memory stars along the heart
    const n = memories.length;
    const scale = Math.min(w * 0.74, h * 0.78) / 34;
    const cx = w / 2;
    const cyAdj = h * 0.5 - 2.5 * scale; // heart spans y -12..17, so nudge up to center it
    const pts = memories.map((m, i) => {
      const t = (i + 0.5) * (TWO_PI / Math.max(n, 1));
      const p = heartPoint(t);
      return {
        x: cx + p.x * scale + (rnd() - 0.5) * 3,
        y: cyAdj + p.y * scale + (rnd() - 0.5) * 3,
        phase: rnd() * TWO_PI,
      };
    });

    // cumulative lengths for the draw-in animation (closed loop)
    const lens = [0];
    let total = 0;
    for (let i = 0; i < pts.length; i += 1) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      total += Math.hypot(b.x - a.x, b.y - a.y);
      lens.push(total);
    }

    sceneRef.current = {
      w, h, dpr, ambient, pts, lens, totalLen: total,
      moon: { x: w * 0.85, y: h * 0.15, r: Math.max(14, Math.min(w, h) * 0.045) },
    };
  }, [memories]);

  // ---- painting ----
  const paint = useCallback(
    (now) => {
      const canvas = canvasRef.current;
      const scene = sceneRef.current;
      if (!canvas || !scene) return;
      const ctx = canvas.getContext('2d');
      const { w, h, dpr, ambient, pts, lens, totalLen, moon } = scene;
      const mems = memories || [];
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // night gradient
      const bg = ctx.createLinearGradient(0, 0, 0, h);
      bg.addColorStop(0, '#04030f');
      bg.addColorStop(0.55, '#140b26');
      bg.addColorStop(1, '#2a0a1e');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // aurora — three soft drifting glows
      ctx.globalCompositeOperation = 'lighter';
      const blobs = [
        ['rgba(255, 95, 162, 0.085)', 0.28, 0.22, 0.00003, 0],
        ['rgba(139, 59, 255, 0.075)', 0.7, 0.3, 0.000023, 2.1],
        ['rgba(91, 192, 255, 0.05)', 0.5, 0.12, 0.000017, 4.2],
      ];
      blobs.forEach(([color, fx, fy, sp, ph]) => {
        const bx = w * (fx + 0.08 * Math.sin(now * sp + ph));
        const by = h * (fy + 0.05 * Math.cos(now * sp * 1.3 + ph));
        const g = ctx.createRadialGradient(bx, by, 0, bx, by, w * 0.42);
        g.addColorStop(0, color);
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      });
      ctx.globalCompositeOperation = 'source-over';

      // moon — soft glow + crescent
      const mg = ctx.createRadialGradient(moon.x, moon.y, moon.r * 0.4, moon.x, moon.y, moon.r * 3.4);
      mg.addColorStop(0, 'rgba(255, 244, 214, 0.25)');
      mg.addColorStop(1, 'rgba(255, 244, 214, 0)');
      ctx.fillStyle = mg;
      ctx.fillRect(moon.x - moon.r * 4, moon.y - moon.r * 4, moon.r * 8, moon.r * 8);
      ctx.beginPath();
      ctx.arc(moon.x, moon.y, moon.r, 0, TWO_PI);
      ctx.fillStyle = '#ffefcf';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(moon.x - moon.r * 0.42, moon.y - moon.r * 0.18, moon.r * 0.92, 0, TWO_PI);
      ctx.fillStyle = 'rgba(20, 11, 38, 0.92)';
      ctx.fill();

      // ambient stars
      ambient.forEach((s) => {
        const a = reduced ? s.base : s.base + Math.sin(now * s.speed + s.phase) * s.amp;
        ctx.globalAlpha = Math.max(0.05, Math.min(1, a));
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, TWO_PI);
        ctx.fillStyle = '#fff';
        ctx.fill();
        if (s.glint) {
          ctx.globalAlpha *= 0.6;
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.moveTo(s.x - s.r * 4, s.y);
          ctx.lineTo(s.x + s.r * 4, s.y);
          ctx.moveTo(s.x, s.y - s.r * 4);
          ctx.lineTo(s.x, s.y + s.r * 4);
          ctx.stroke();
        }
      });
      ctx.globalAlpha = 1;

      // shooting stars
      if (!reduced) {
        const sh = shootsRef.current;
        if (!sh.next) sh.next = now + 2500;
        if (now >= sh.next && sh.list.length < 2) {
          const dir = Math.random() > 0.5 ? 1 : -1;
          const ang = (35 + Math.random() * 20) * (Math.PI / 180);
          const speed = 0.5 + Math.random() * 0.25;
          sh.list.push({
            x: w * (0.15 + Math.random() * 0.7),
            y: h * (0.04 + Math.random() * 0.25),
            vx: Math.cos(ang) * speed * dir,
            vy: Math.sin(ang) * speed,
            born: now,
            life: 650 + Math.random() * 250,
          });
          sh.next = now + 6500 + Math.random() * 6500;
        }
        sh.list = sh.list.filter((s) => now - s.born < s.life);
        sh.list.forEach((s) => {
          const age = now - s.born;
          const fade = 1 - age / s.life;
          const x = s.x + s.vx * age;
          const y = s.y + s.vy * age;
          const tail = 85;
          const grad = ctx.createLinearGradient(x, y, x - s.vx * tail, y - s.vy * tail);
          grad.addColorStop(0, `rgba(255,255,255,${0.85 * fade})`);
          grad.addColorStop(1, 'rgba(255,255,255,0)');
          ctx.strokeStyle = grad;
          ctx.lineWidth = 1.6;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x - s.vx * tail, y - s.vy * tail);
          ctx.stroke();
        });
      }

      // constellation line (draws itself in, then breathes)
      if (pts.length > 1) {
        const elapsed = reduced ? Infinity : now - startRef.current;
        const progress = Math.min(1, elapsed / 3600);
        const target = totalLen * progress;
        const breathe = progress >= 1 && !reduced ? 0.12 * Math.sin(now * 0.0012) : 0;
        const grad = ctx.createLinearGradient(0, 0, w, h);
        grad.addColorStop(0, '#ff6fa8');
        grad.addColorStop(1, '#b07cff');
        ctx.strokeStyle = grad;
        ctx.globalAlpha = 0.5 + breathe;
        ctx.lineWidth = 1.3;
        ctx.shadowColor = 'rgba(255, 111, 168, 0.8)';
        ctx.shadowBlur = 6;
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i <= pts.length; i += 1) {
          const a = pts[i - 1];
          const b = pts[i % pts.length];
          if (lens[i] <= target) {
            ctx.lineTo(b.x, b.y);
          } else {
            const seg = lens[i] - lens[i - 1];
            const f = seg > 0 ? (target - lens[i - 1]) / seg : 0;
            if (f > 0) ctx.lineTo(a.x + (b.x - a.x) * f, a.y + (b.y - a.y) * f);
            break;
          }
        }
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
      }

      // memory stars
      pts.forEach((p, i) => {
        const m = mems[i];
        const active = hoverRef.current === i || selectedRef.current === i;
        const pulse = reduced ? 0.5 : 0.5 + 0.5 * Math.sin(now * 0.0021 + p.phase);
        const r = (m && m.event ? 4.1 : 3.2) + pulse * 0.9 + (active ? 1.6 : 0);
        const halo = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 4.4);
        halo.addColorStop(0, `rgba(255, 170, 200, ${active ? 0.5 : 0.26})`);
        halo.addColorStop(1, 'rgba(255, 170, 200, 0)');
        ctx.fillStyle = halo;
        ctx.fillRect(p.x - r * 5, p.y - r * 5, r * 10, r * 10);
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, TWO_PI);
        ctx.fillStyle = '#fff3da';
        ctx.fill();
        if (active) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, r + 5, 0, TWO_PI);
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.65)';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
        if (m && m.event) {
          ctx.font = '13px serif';
          ctx.textAlign = 'center';
          ctx.fillText(m.event.emoji, p.x, p.y - r - 8);
        }
        if (active && m) {
          const label = m.event ? `${m.event.emoji} ${m.event.title}` : shortDate(m.date);
          ctx.font = '600 16px Caveat, cursive';
          ctx.textAlign = 'center';
          ctx.shadowColor = 'rgba(0,0,0,0.8)';
          ctx.shadowBlur = 5;
          ctx.fillStyle = '#ffe2ee';
          ctx.fillText(label, p.x, p.y - r - (m.event ? 24 : 12));
          ctx.shadowBlur = 0;
        }
      });
    },
    [memories, reduced]
  );

  // ---- lifecycle: build, animate, resize ----
  useEffect(() => {
    if (!memories) return undefined;
    buildScene();
    startRef.current = performance.now();
    let running = true;

    const loop = (now) => {
      if (!running) return;
      paint(now);
      if (!reduced) rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    const onVis = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(rafRef.current);
      } else if (!reduced) {
        running = true;
        rafRef.current = requestAnimationFrame(loop);
      }
    };
    document.addEventListener('visibilitychange', onVis);

    const ro = new ResizeObserver(() => {
      buildScene();
      if (reduced) paint(performance.now());
    });
    if (wrapRef.current) ro.observe(wrapRef.current);

    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
      document.removeEventListener('visibilitychange', onVis);
      ro.disconnect();
    };
  }, [memories, buildScene, paint, reduced]);

  // ---- pointer interaction ----
  const starAt = (e) => {
    const scene = sceneRef.current;
    const canvas = canvasRef.current;
    if (!scene || !canvas) return -1;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    let best = -1;
    let bestD = 24;
    scene.pts.forEach((p, i) => {
      const d = Math.hypot(p.x - x, p.y - y);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    return best;
  };

  const onMove = (e) => {
    const i = starAt(e);
    hoverRef.current = i;
    if (canvasRef.current) canvasRef.current.style.cursor = i >= 0 ? 'pointer' : 'default';
    if (reduced) paint(performance.now());
  };

  const onClick = (e) => {
    const i = starAt(e);
    setSelected(i >= 0 ? i : null);
    if (reduced) paint(performance.now());
  };

  const sel = selected != null && memories ? memories[selected] : null;
  const selPos = selected != null && sceneRef.current ? sceneRef.current.pts[selected] : null;
  const cardStyle = selPos
    ? {
        left: Math.max(12, Math.min((sceneRef.current.w || 0) - 232, selPos.x - 110)),
        top: Math.max(12, selPos.y - 130),
      }
    : null;

  const counts = (m) => {
    const bits = [];
    if (m.notes) bits.push(`${m.notes} note${m.notes === 1 ? '' : 's'}`);
    if (m.photos) bits.push(`${m.photos} photo${m.photos === 1 ? '' : 's'}`);
    if (m.voices) bits.push(`${m.voices} voice note${m.voices === 1 ? '' : 's'}`);
    return bits.join(' · ');
  };

  return (
    <div className="aj-sky-section">
      <div className="aj-days-head">
        <h2 className="aj-days-title">written in the stars</h2>
        <p className="aj-days-sub">I checked the sky — it was always going to be you.</p>
      </div>

      <div className="aj-sky-wrap" ref={wrapRef}>
        <canvas
          ref={canvasRef}
          className="aj-sky"
          onPointerMove={onMove}
          onClick={onClick}
          aria-label="our story drawn as a constellation"
        />
        {memories === null && <div className="aj-sky-center aj-muted">charting our sky…</div>}
        {memories && memories.length === 0 && (
          <div className="aj-sky-center aj-muted">
            {err || 'our sky is waiting for its first star — write us a memory. ✨'}
          </div>
        )}

        {sel && cardStyle && (
          <div className="aj-star-card" style={cardStyle}>
            <button type="button" className="aj-del aj-star-card-close" onClick={() => setSelected(null)} aria-label="close">
              ×
            </button>
            {sel.event && (
              <div className="aj-star-card-event">
                {sel.event.emoji} {sel.event.title}
              </div>
            )}
            <div className="aj-star-card-date">{prettyDate(sel.date)}</div>
            {counts(sel) && <div className="aj-star-card-counts">{counts(sel)}</div>}
            {selected === 0 && <div className="aj-star-card-first">where it all began ✨</div>}
          </div>
        )}
      </div>

      {memories && memories.length > 0 && (
        <p className="aj-sky-foot">
          every star up there is a day we made ours — tap one. 🌠
          <span className="aj-sky-count">
            {memories.length} star{memories.length === 1 ? '' : 's'} and counting
          </span>
        </p>
      )}
    </div>
  );
}
